import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  Organisation,
  OrganisationCreate,
  OrganisationUpdate,
  OrganisationDeleteRequest,
  OrganisationQuotaUsage,
  OrganisationMember,
  Region,
} from '../types/api';
import apiService from '../services/api';
import Logger from '../utils/logger';
import { showErrorToast } from '../utils/toastUtils';
import { ErrorMessages } from '../constants/messages';
import { useAuth } from './AuthContext';

interface OrganisationContextType {
  // Current selected organization
  currentOrganisation: Organisation | null;

  // All user's organizations
  userOrganisations: Organisation[];

  // User's role in current organization
  currentUserRole: 'owner' | 'admin' | 'member' | null;

  // Loading states
  loading: boolean;
  organisationsLoading: boolean;

  // Quota usage
  quotaUsage: OrganisationQuotaUsage | null;

  // Actions
  selectOrganisation: (orgId: string) => Promise<void>;
  refreshOrganisations: () => Promise<void>;
  createOrganisation: (data: OrganisationCreate) => Promise<Organisation>;
  updateOrganisation: (orgId: string, data: OrganisationUpdate) => Promise<void>;
  deleteOrganisation: (orgId: string, confirmData: OrganisationDeleteRequest) => Promise<void>;
  refreshQuotaUsage: () => Promise<void>;

  // Region selection
  selectedRegion: Region | null;
  selectRegion: (region: Region | null) => void;

  // Permission helpers
  canManageMembers: () => boolean;
  canManageSettings: () => boolean;
  canDeleteOrganisation: () => boolean;
  canChangePlan: () => boolean;
}

const OrganisationContext = createContext<OrganisationContextType | undefined>(undefined);

interface OrganisationProviderProps {
  children: ReactNode;
}

export const OrganisationProvider: React.FC<OrganisationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [currentOrganisation, setCurrentOrganisation] = useState<Organisation | null>(null);
  const [userOrganisations, setUserOrganisations] = useState<Organisation[]>([]);
  const [currentUserRole, setCurrentUserRole] = useState<'owner' | 'admin' | 'member' | null>(null);
  const [quotaUsage, setQuotaUsage] = useState<OrganisationQuotaUsage | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [loading, setLoading] = useState(false);
  const [organisationsLoading, setOrganisationsLoading] = useState(true);

  // Load user's organizations on mount or when user changes
  useEffect(() => {
    const initOrganisations = async () => {
      if (isAuthenticated && user) {
        try {
          setOrganisationsLoading(true);
          const organisations = await apiService.getOrganisations();
          setUserOrganisations(organisations);

          // Try to restore last selected organization from localStorage
          const savedOrgId = localStorage.getItem('currentOrganisationId');
          if (savedOrgId) {
            const savedOrg = organisations.find(org => org.id === savedOrgId);
            if (savedOrg) {
              await selectOrganisation(savedOrgId);
            } else {
              // Saved org not found, select first available
              if (organisations.length > 0) {
                await selectOrganisation(organisations[0].id);
              }
            }
          } else if (organisations.length > 0) {
            // No saved org, select first available
            await selectOrganisation(organisations[0].id);
          }
        } catch (error) {
          Logger.error('Failed to load organizations', error);
          showErrorToast(error, ErrorMessages.ORG_LOAD_FAILED);
        } finally {
          setOrganisationsLoading(false);
        }
      } else {
        // Clear organization state when user logs out
        setUserOrganisations([]);
        setCurrentOrganisation(null);
        setCurrentUserRole(null);
        setQuotaUsage(null);
        localStorage.removeItem('currentOrganisationId');
        setOrganisationsLoading(false);
      }
    };

    initOrganisations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Select an organization and load its details
  const selectOrganisation = useCallback(async (orgId: string) => {
    try {
      setLoading(true);

      // Fetch organization details
      const org = await apiService.getOrganisation(orgId);
      setCurrentOrganisation(org);

      // Fetch quota usage
      const quota = await apiService.getOrganisationQuotaUsage(orgId);
      setQuotaUsage(quota);

      // Fetch members to determine current user's role
      const members = await apiService.getOrganisationMembers(orgId);
      const currentUserMember = members.find((m: OrganisationMember) => m.utilisateur_id === user?.id);
      setCurrentUserRole(currentUserMember?.role_organisation || null);

      // Persist selection
      localStorage.setItem('currentOrganisationId', orgId);

      // Reset region selection when changing org
      setSelectedRegion(null);
    } catch (error) {
      Logger.error('Failed to select organization', error);
      showErrorToast(error, ErrorMessages.ORG_LOAD_FAILED);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user]);

  const selectRegion = useCallback((region: Region | null) => {
    setSelectedRegion(region);
  }, []);

  // Refresh organizations list
  const refreshOrganisations = useCallback(async () => {
    try {
      setOrganisationsLoading(true);
      const organisations = await apiService.getOrganisations();
      setUserOrganisations(organisations);

      // If current org is no longer in list, clear it
      if (currentOrganisation && !organisations.find(o => o.id === currentOrganisation.id)) {
        setCurrentOrganisation(null);
        setCurrentUserRole(null);
        setQuotaUsage(null);
        localStorage.removeItem('currentOrganisationId');

        // Select first available org if any
        if (organisations.length > 0) {
          await selectOrganisation(organisations[0].id);
        }
      }
    } catch (error) {
      Logger.error('Failed to refresh organizations', error);
      showErrorToast(error, ErrorMessages.ORG_LOAD_FAILED);
      throw error;
    } finally {
      setOrganisationsLoading(false);
    }
  }, [currentOrganisation, selectOrganisation]);

  // Create new organization
  const createOrganisation = useCallback(async (data: OrganisationCreate): Promise<Organisation> => {
    try {
      const newOrg = await apiService.createOrganisation(data);

      // Refresh organizations list
      await refreshOrganisations();

      // Auto-select the newly created organization
      await selectOrganisation(newOrg.id);

      return newOrg;
    } catch (error) {
      Logger.error('Failed to create organization', error);
      showErrorToast(error, ErrorMessages.ORG_CREATE_FAILED);
      throw error;
    }
  }, [refreshOrganisations, selectOrganisation]);

  // Update organization
  const updateOrganisation = useCallback(async (orgId: string, data: OrganisationUpdate) => {
    try {
      const updatedOrg = await apiService.updateOrganisation(orgId, data);

      // Update in list
      setUserOrganisations(prev =>
        prev.map(org => org.id === orgId ? updatedOrg : org)
      );

      // Update current if it's the selected one
      if (currentOrganisation?.id === orgId) {
        setCurrentOrganisation(updatedOrg);
      }
    } catch (error) {
      Logger.error('Failed to update organization', error);
      showErrorToast(error, ErrorMessages.ORG_UPDATE_FAILED);
      throw error;
    }
  }, [currentOrganisation]);

  // Delete organization
  const deleteOrganisation = useCallback(async (orgId: string, confirmData: OrganisationDeleteRequest) => {
    try {
      await apiService.deleteOrganisation(orgId, confirmData);

      // Remove from list
      setUserOrganisations(prev => prev.filter(org => org.id !== orgId));

      // If it was the current org, clear state without auto-selecting
      // (the caller handles navigation, the target page will handle org selection)
      if (currentOrganisation?.id === orgId) {
        setCurrentOrganisation(null);
        setCurrentUserRole(null);
        setQuotaUsage(null);
        localStorage.removeItem('currentOrganisationId');
      }
    } catch (error) {
      Logger.error('Failed to delete organization', error);
      showErrorToast(error, ErrorMessages.ORG_DELETE_FAILED);
      throw error;
    }
  }, [currentOrganisation]);

  // Refresh quota usage
  const refreshQuotaUsage = useCallback(async () => {
    if (currentOrganisation) {
      try {
        const quota = await apiService.getOrganisationQuotaUsage(currentOrganisation.id);
        setQuotaUsage(quota);
      } catch (error) {
        Logger.error('Failed to refresh quota usage', error);
        showErrorToast(error, 'Echec du chargement des quotas');
        throw error;
      }
    }
  }, [currentOrganisation]);

  // Permission helpers
  const canManageMembers = useCallback(() => {
    return currentUserRole === 'owner' || currentUserRole === 'admin';
  }, [currentUserRole]);

  const canManageSettings = useCallback(() => {
    return currentUserRole === 'owner' || currentUserRole === 'admin';
  }, [currentUserRole]);

  const canDeleteOrganisation = useCallback(() => {
    return currentUserRole === 'owner';
  }, [currentUserRole]);

  const canChangePlan = useCallback(() => {
    return currentUserRole === 'owner';
  }, [currentUserRole]);

  const value: OrganisationContextType = {
    currentOrganisation,
    userOrganisations,
    currentUserRole,
    loading,
    organisationsLoading,
    quotaUsage,
    selectOrganisation,
    refreshOrganisations,
    createOrganisation,
    updateOrganisation,
    deleteOrganisation,
    refreshQuotaUsage,
    selectedRegion,
    selectRegion,
    canManageMembers,
    canManageSettings,
    canDeleteOrganisation,
    canChangePlan,
  };

  return (
    <OrganisationContext.Provider value={value}>
      {children}
    </OrganisationContext.Provider>
  );
};

export const useOrganisation = (): OrganisationContextType => {
  const context = useContext(OrganisationContext);
  if (context === undefined) {
    throw new Error('useOrganisation must be used within an OrganisationProvider');
  }
  return context;
};
